export type CustomerMetricScope = Record<string, string | null | undefined>;

export type CustomerMetricQualification = Record<string, string | boolean | number | null | undefined>;

export type CustomerMetric = {
  semantic_key: string;
  value: number;
  grain: string;
  population_key: string;
  window_kind: string;
  window_start?: string | null;
  window_end?: string | null;
  scope?: CustomerMetricScope | null;
  qualification?: CustomerMetricQualification | null;
  review_state?: string | null;
  source?: string | null;
};

function normalizeText(value: unknown): string {
  return String(value ?? '').trim();
}

function normalizeScope(scope?: CustomerMetricScope | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!scope || typeof scope !== 'object') return out;
  Object.entries(scope).forEach(([key, value]) => {
    const text = normalizeText(value);
    if (text) out[key] = text;
  });
  return out;
}

function normalizeScopeSignature(scope?: CustomerMetricScope | null): string {
  const normalized = normalizeScope(scope);
  return Object.entries(normalized)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${value}`)
    .join('|');
}

function normalizeWindowEdge(value?: string | null): string {
  return normalizeText(value);
}

export function customerMetricsSharePopulation(
  left?: CustomerMetric | null,
  right?: CustomerMetric | null,
): boolean {
  if (!left || !right) return false;
  if (normalizeText(left.population_key) !== normalizeText(right.population_key)) return false;
  if (normalizeText(left.window_kind) !== normalizeText(right.window_kind)) return false;
  if (normalizeWindowEdge(left.window_start) !== normalizeWindowEdge(right.window_start)) return false;
  if (normalizeWindowEdge(left.window_end) !== normalizeWindowEdge(right.window_end)) return false;
  return normalizeScopeSignature(left.scope) === normalizeScopeSignature(right.scope);
}

export function findCustomerMetric(
  metrics: Array<CustomerMetric | null | undefined> | null | undefined,
  semanticKey: string,
): CustomerMetric | null {
  const key = normalizeText(semanticKey);
  if (!key || !Array.isArray(metrics)) return null;
  return metrics.find((metric) => normalizeText(metric?.semantic_key) === key) || null;
}

export function metricUsesSignedScanWindow(
  metric?: CustomerMetric | null,
  signedScanBoundary?: string | null,
): boolean {
  if (!metric) return false;
  if (normalizeText(metric.window_kind) !== 'SINCE_SIGNED_SCAN') return false;
  const start = normalizeWindowEdge(metric.window_start);
  if (!start) return false;
  const boundary = normalizeWindowEdge(signedScanBoundary);
  if (!boundary) return true;
  return start === boundary;
}
